// src/pages/DashboardPage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable';
import Button from '../../components/ui/Button.js';
import Card from '../../components/ui/Card.js';
import Input from '../../components/ui/Input.js';
import { uploadFileApi } from '../../api/uploadFile/fileupload.api.js';
import { getAllCompany } from '../../api/company/company.api.js';
import { getAllLocations } from '../../api/Alllocations/locations.api.js';
import { PDFDocument } from 'pdf-lib';
import './DashboardPage.css';

const compressPdfFile = async file => {
  if (!file) {
    return file;
  }

  if (typeof File !== 'function') {
    return file;
  }

  try {
    const originalBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(originalBuffer, { updateMetadata: false });
    const compressedBytes = await pdfDoc.save({ useObjectStreams: true });
    const compressedBlob = new Blob([compressedBytes], { type: 'application/pdf' });

    if (!compressedBlob.size || compressedBlob.size >= file.size) {
      return file;
    }

    return new File([compressedBlob], file.name, {
      type: 'application/pdf',
      lastModified: file.lastModified
    });
  } catch (compressionError) {
    console.error('Error compressing PDF before upload:', compressionError);
    return file;
  }
};

const DashboardPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [formData, setFormData] = useState({
    companyName: '',
    mdsNumber: '',
    manualType: '',
    location: ''
  });
  const [error, setError] = useState('');

  // console.log("Dashboard: ",user)

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        // Fetch companies
        const res = await getAllCompany();
        const data = res.data?.data || [];
        // console.log('Company API Response:', data);
        const opts = data.map(c => ({ value: c.companyName, label: c.companyName }));
        setCompanyOptions(opts);

        // Fetch locations
        try {
          const locRes = await getAllLocations();
          const locData = locRes.data?.data || [];
          // console.log('Location API Response:', locData);

          // Check if location API returned valid data
          if (locData.length > 0) {
            // Check if location data has the correct property name
            const locOpts = locData.map(l => {
              // Try different possible property names
              const locationName = l.locationName || l.location || l.name || l.companyName;
              return { value: locationName, label: locationName };
            }).filter(opt => opt.value); // Remove any undefined values

            setLocationOptions(locOpts);
          } 
        } catch (locationError) {
          console.error('Error fetching locations:', locationError);
        }

      } catch (error) {
        console.error('Error fetching companies:', error);
        setError('Failed to fetch companies. Please try again later.');
      }
    };
    fetchCompanies();
  }, []);

  const handleSelectChange = option => {
    setFormData(prev => ({ ...prev, companyName: option ? option.value : '' }));
    setError('');
  };

  const handleCreateOption = inputValue => {
    const newOption = { value: inputValue, label: inputValue };
    setCompanyOptions(prev => [...prev, newOption]);
    setFormData(prev => ({ ...prev, companyName: inputValue }));
  };

  const handleLocationSelectChange = option => {
    setFormData(prev => ({ ...prev, location: option ? option.value : '' }));
    setError('');
  };

  const handleCreateLocationOption = inputValue => {
    const newOption = { value: inputValue, label: inputValue };
    setLocationOptions(prev => [...prev, newOption]);
    setFormData(prev => ({ ...prev, location: inputValue }));
  };

  const handleInputChange = field => e => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setError('Please select a PDF file');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError('File size must be less than 20MB');
      return;
    }
    setSelectedFile(file);
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!selectedFile) {
      setError('Please select a PDF file to upload');
      return;
    }
    const { companyName, mdsNumber, manualType, location } = formData;
    if (!companyName || !mdsNumber || !manualType || !location) {
      setError('Please fill in all fields');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      setUploadProgress(5);
      const fileToUpload = await compressPdfFile(selectedFile);

      if (fileToUpload !== selectedFile) {
        console.log(
          `Compressed PDF from ${(selectedFile.size / 1024 / 1024).toFixed(2)}MB to ${(fileToUpload.size / 1024 / 1024).toFixed(2)}MB`
        );
        setSelectedFile(fileToUpload);
      }

      const uploadInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) { clearInterval(uploadInterval); return 90; }
          return prev + 10;
        });
      }, 200);

      const response = await uploadFileApi(
        fileToUpload,
        companyName,
        mdsNumber,
        manualType,
        location
      );
      clearInterval(uploadInterval);
      setUploadProgress(100);

      if (response.error) {
        setError(`Upload failed: ${response.data?.error || 'Unknown error'}`);
        setIsUploading(false);
        setUploadProgress(0);
      } else {
        setTimeout(() => {
          alert('Manual uploaded successfully!');
          setSelectedFile(null);
          setUploadProgress(0);
          setIsUploading(false);
          setFormData({ companyName: '', mdsNumber: '', manualType: '', location: '' });
        }, 500);
      }
    } catch {
      setError('Upload failed. Please try again.');
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const manualTypes = [
    { value: '', label: 'Select Manual Type' },
    { value: 'spare_manual', label: 'Spare manual' },
    { value: 'operation_manual', label: 'Operation manual' },
    { value: 'other_manual', label: 'Other' },
  ];

  return (
    <div className="dashboard-page">
      <div className="dashboard-page__container">
        <header className="dashboard-header">
          <div className="dashboard-header__content">
            <div className="dashboard-header__left">
              {/* <div className="dashboard-header__logo">
                <img src="/clearpack.png" alt="Company Logo" className="dashboard-header__logo-image" />
              </div> */}
              <div className="dashboard-header__user">
                <div className="dashboard-header__avatar">
                  {(user?.displayName || user || 'User').charAt(0).toUpperCase()}
                </div>
                <div className="dashboard-header__info">
                  <h1 className="dashboard-header__title">
                    Welcome, {user?.displayName || user || 'User'}!
                  </h1>
                  <p className="dashboard-header__subtitle">{user?.role || 'User'}</p>
                </div>
              </div>
            </div>
            <div className="dashboard-header__actions">
              {/* Show Admin Panel button only for admin users */}
              {user?.role === 'admin' && (
                <Button
                  variant="primary"
                  onClick={() => navigate('/dashboard/adminpanel')}
                  className="dashboard-header__admin"
                >
                  👥 Admin Panel
                </Button>
              )}
              <Button
                variant="outline"
                onClick={onLogout}
                className="dashboard-header__logout"
              >
                Sign Out
              </Button>
            </div>
          </div>
        </header>

        <main className="dashboard-content">
          <div className="dashboard-grid">
            <Card
              title="Upload User Manual"
              subtitle="Upload your company's user manual in PDF format"
              variant="elevated"
              className="dashboard-card dashboard-card--full-width"
            >
              <form onSubmit={handleSubmit} className="upload-form">
                {error && (
                  <div className="upload-form__error-banner">{error}</div>
                )}

                <div className="upload-form__field">
                  <label htmlFor="companyName">Company Name</label>
                  <CreatableSelect
                    id="companyName"
                    isClearable
                    options={companyOptions}
                    value={
                      formData.companyName
                        ? {
                            value: formData.companyName,
                            label: formData.companyName
                          }
                        : null
                    }
                    onChange={handleSelectChange}
                    onCreateOption={handleCreateOption}
                    placeholder="Type or select company…"
                    classNamePrefix="react-select"
                  />
                </div>

                <div className="upload-form__field">
                  <label htmlFor="location">Location</label>
                  <CreatableSelect
                    id="location"
                    isClearable
                    options={locationOptions}
                    value={
                      formData.location
                        ? {
                            value: formData.location,
                            label: formData.location
                          }
                        : null
                    }
                    onChange={handleLocationSelectChange}
                    onCreateOption={handleCreateLocationOption}
                    placeholder="Type or select location…"
                    classNamePrefix="react-select"
                  />
                </div>

                <div className="upload-form__field">
                  <Input
                    label="MDS Number"
                    name="mdsNumber"
                    placeholder="Enter your MDS number"
                    value={formData.mdsNumber}
                    onChange={handleInputChange('mdsNumber')}
                    required
                  />
                </div>

                <div className="upload-form__field">
                  <label className="upload-form__label">
                    Manual Type
                    <span className="upload-form__required">*</span>
                  </label>
                  <select
                    name="manualType"
                    value={formData.manualType}
                    onChange={handleInputChange('manualType')}
                    className="upload-form__select"
                    required
                  >
                    {manualTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="upload-form__field">
                  <label className="upload-form__label">
                    PDF File
                    <span className="upload-form__required">*</span>
                  </label>
                  <div
                    className="upload-form__file-input"
                    onClick={() => document.getElementById('file-input').click()}
                  >
                    <input
                      id="file-input"
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="upload-form__file"
                      disabled={isUploading}
                    />
                    <div className="upload-form__file-info">
                      {selectedFile ? (
                        <div className="upload-form__file-details">
                          <span className="upload-form__file-name">
                            {selectedFile.name}
                          </span>
                          <span className="upload-form__file-size">
                            ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                      ) : (
                        <span className="upload-form__file-placeholder">
                          Click to choose PDF file (max 20MB)
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {isUploading && (
                  <div className="upload-form__progress">
                    <div className="upload-form__progress-bar">
                      <div
                        className="upload-form__progress-fill"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <span className="upload-form__progress-text">
                      Uploading... {uploadProgress}%
                    </span>
                  </div>
                )}

                <div className="upload-form__actions">
                  <Button
                    type="submit"
                    variant="primary"
                    size="large"
                    loading={isUploading}
                    disabled={!selectedFile}
                    className="upload-form__submit"
                  >
                    {isUploading ? 'Uploading...' : 'Upload Manual'}
                  </Button>
                </div>

                <div className="upload-form__footer">
                  <p className="upload-form__info">
                    <strong>Supported formats:</strong> PDF only
                    <br />
                    <strong>Maximum file size:</strong> 20MB
                    <br />
                    <strong>Manual types:</strong> Spare manual, Operation manual,
                    Other
                  </p>
                </div>
              </form>
            </Card>
          </div>
        </main>
      </div>


    </div>
  );
};

export default DashboardPage;
